import React from "react";
import {
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
    Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import type { Jar } from "./JarDetailsDialog";

interface JarCardProps {
    jar: Jar;
    onMenuOpen: (e: React.MouseEvent<HTMLElement>, jarId: string) => void;
    onClick?: (jar: Jar) => void;
}

const COLORS = ["#c82f27", "#007AFF", "#34C759", "#AF52DE", "#FF9500"];

function getInitials(name: string): string {
    return name
        .split(" ")
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("")
        .slice(0, 2);
}

function formatDollars(cents: number) {
    const abs = Math.abs(cents / 100);
    return `$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function JarCard({ jar, onMenuOpen, onClick }: JarCardProps) {
    const targetDollars = jar.targetAmountCents / 100;
    const progressPercent = jar.targetAmountCents > 0
        ? (jar.currentAmountCents / jar.targetAmountCents) * 100
        : 0;
    const remaining = jar.targetAmountCents - jar.currentAmountCents;
    const isShared = jar.members.length > 1;

    // Calculate monthly target from targetDate (only when date is set)
    const monthlyTarget = (() => {
        if (!jar.targetDate) return null;
        const remainingDollars = (jar.targetAmountCents - jar.currentAmountCents) / 100;
        const due = new Date(jar.targetDate);
        const now = new Date();
        const monthsLeft =
            (due.getFullYear() - now.getFullYear()) * 12 +
            (due.getMonth() - now.getMonth());
        return monthsLeft > 0 ? remainingDollars / monthsLeft : remainingDollars;
    })();

    return (
        <Card 
            sx={{ 
                height: "100%", 
                cursor: onClick ? "pointer" : "default",
                "&:hover": onClick ? { boxShadow: 3 } : {}
            }}
            onClick={() => onClick?.(jar)}
        >
            <CardContent>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {/* Color Icon */}
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1.5,
                                backgroundColor: jar.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 24,
                            }}
                        >
                            {jar.icon}
                        </Box>

                        {/* Title and Amount */}
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {jar.name}
                                </Typography>
                                {isShared && (
                                    <Chip label="Shared" size="small" color="primary" variant="outlined" />
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {formatDollars(jar.currentAmountCents)} saved
                            </Typography>
                        </Box>
                    </Stack>

                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onMenuOpen(e, jar.id); }}
                        sx={{ mt: -0.5 }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Shared Avatars */}
                {isShared ? (
                    <Stack sx={{ mb: 2, alignItems: "flex-start" }}>
                        <AvatarGroup max={3}>
                            {jar.members.map((m, index) => (
                                <Avatar
                                    key={m.userId}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: 12,
                                        backgroundColor: COLORS[index % COLORS.length],
                                    }}
                                >
                                    {getInitials(m.user.name ?? m.user.email ?? "?")}
                                </Avatar>
                            ))}
                        </AvatarGroup>
                    </Stack>
                ) : (
                    <Box sx={{ mb: 2, height: 28 }} />
                )}

                {/* Savings Goal Section */}
                {jar.targetAmountCents > 0 && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Goal
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatDollars(jar.targetAmountCents)}
                                </Typography>
                            </Stack>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(progressPercent, 100)}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: "grey.300",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 4,
                                  backgroundColor: "#1a1a1a",
                                }
                              }}
                            />
                        </Box>

                        {/* Progress Info */}
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {progressPercent.toFixed(1)}% complete
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDollars(remaining)} remaining
                            </Typography>
                        </Stack>

                        {/* <Divider sx={{ my: 1.5 }} /> */}

                        {/* Monthly Target — only shown when a target date is set */}
                        {monthlyTarget !== null && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Monthly Saving Target
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    ${monthlyTarget.toFixed(2)}/month
                                </Typography>
                            </Stack>
                        )}
                    </>
                )}

                {/* Expense Summary Section */}
                {(jar.expenseCount ?? 0) > 0 && (
                    <>
                        {jar.targetAmountCents > 0 && <Divider sx={{ my: 1.5 }} />}

                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Total Expenses
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatDollars(jar.totalExpenseCents ?? 0)}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Transactions
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {jar.expenseCount}
                                </Typography>
                            </Stack>
                            {isShared && (
                                <Tooltip title="Total expenses divided equally among members. View jar details for actual per-person split amounts.">
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            Avg per Person
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatDollars(Math.round((jar.totalExpenseCents ?? 0) / jar.members.length))}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            )}
                        </Stack>
                    </>
                )}

                {/* Empty state when no goal and no expenses */}
                {jar.targetAmountCents <= 0 && (jar.expenseCount ?? 0) === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No savings goal or expenses yet
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}
